import { useCallback, useEffect, useState } from 'react'
import { api } from '../../api/client'
import { useStores } from '../../app/StoreContext'
import type {
  CalendarDerogation,
  CalendarResponse,
  CalendarSlot,
  Holiday,
  LeadTimeRule,
  RejectionReason,
  SupplierLimits,
} from '../../api/types'
import { Button, Card, Empty, ErrorBox, Field, Num, Spinner, Badge, formatDate } from '../../ui/primitives'

type Tab = 'calendar' | 'holidays' | 'limits' | 'leadTimes' | 'reasons'

const TABS: { id: Tab; label: string }[] = [
  { id: 'calendar', label: 'Calendario consegne CEDI' },
  { id: 'holidays', label: 'Giorni festivi' },
  { id: 'limits', label: 'Limiti fornitore' },
  { id: 'leadTimes', label: 'Tempi medi di consegna' },
  { id: 'reasons', label: 'Motivazioni di rifiuto' },
]

export function ConfigPage() {
  const [tab, setTab] = useState<Tab>('calendar')

  return (
    <>
      <div className="page-head">
        <h1>Configurazioni</h1>
      </div>

      <nav className="inline" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <Button key={t.id} variant={tab === t.id ? 'primary' : 'default'} onClick={() => setTab(t.id)}>
            {t.label}
          </Button>
        ))}
      </nav>

      {tab === 'calendar' && <CalendarSection />}
      {tab === 'holidays' && <HolidaysSection />}
      {tab === 'limits' && <LimitsSection />}
      {tab === 'leadTimes' && <LeadTimesSection />}
      {tab === 'reasons' && <ReasonsSection />}
    </>
  )
}

/**
 * Calendario consegne CEDI.
 *
 * È l'unica configurazione in sola lettura, e per un motivo preciso: lo stesso calendario lo
 * legge il motore di previsione per decidere quando le proposte devono essere pronte. Se KPO
 * ne tenesse una copia modificabile, le due letture divergerebbero. Lo diciamo a video invece
 * di mostrare campi disabilitati senza spiegazione.
 */
function CalendarSection() {
  const { selected, storeFilter, storesReady, storeName } = useStores()

  const [standard, setStandard] = useState<CalendarResponse<CalendarSlot>>()
  const [derogations, setDerogations] = useState<CalendarResponse<CalendarDerogation>>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const [from] = useState(() => new Date().toISOString().slice(0, 10))
  const [to] = useState(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + 3)
    return d.toISOString().slice(0, 10)
  })

  const load = useCallback(() => {
    // Senza l'elenco dei punti vendita non si sa se «tutti spuntati» siano tutti, e il filtro
    // partirebbe per esteso: si aspetta. Nessuna selezione, invece, non è un vicolo cieco —
    // vale «tutti i punti vendita visibili», come nelle altre schermate.
    if (!storesReady) return

    setLoading(true)
    setError(undefined)

    Promise.all([
      api.get<CalendarResponse<CalendarSlot>>('/config/cedi-calendar/standard', { storeCode: storeFilter }),
      api.get<CalendarResponse<CalendarDerogation>>('/config/cedi-calendar/derogations', {
        storeCode: storeFilter,
        from,
        to,
      }),
    ])
      .then(([s, d]) => {
        setStandard(s)
        setDerogations(d)
      })
      .catch(setError)
      .finally(() => setLoading(false))
  }, [storeFilter, storesReady, from, to])

  useEffect(load, [load])

  if (loading || !storesReady) return <Spinner />
  if (error) return <ErrorBox error={error} onRetry={load} />

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const dayLabels = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato']

  // Con una selezione esplicita le righe sono quelle: un punto vendita scelto che non riceve
  // niente dal centro va visto, con la riga vuota, altrimenti sembrerebbe dimenticato. Senza
  // selezione le righe sono quelle che il server ha restituito — tutti i visibili.
  const rows =
    selected.length > 0
      ? selected
      : [...new Set(standard?.entries.map((e) => e.store) ?? [])].sort()

  return (
    <>
      {standard && !standard.editable && (
        <div className="notice notice-info">
          <strong>Sola lettura</strong>
          <p>{standard.source}</p>
        </div>
      )}

      <Card
        title={
          selected.length > 0 ?
            `Settimana tipo · ${selected.length} punti vendita selezionati`
          : `Settimana tipo · tutti i punti vendita visibili (${rows.length})`
        }
      >
        {rows.length === 0 ?
          <Empty>Nessun punto vendita riceve merce dal centro di distribuzione.</Empty>
        : <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Punto vendita</th>
                {dayLabels.map((d) => (
                  <th key={d}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((code) => (
                <tr key={code}>
                  <td>
                    <span className="muted">{code}</span> {storeName(code)}
                  </td>
                  {days.map((day) => {
                    const slot = standard?.entries.find((e) => e.store === code && e.day === day)
                    return (
                      <td key={day}>
                        {slot ? (
                          <Badge tone={slot.slot === 'Morning' ? 'info' : 'warn'}>{slotLabel(slot.slot)}</Badge>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        }
        <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
          Una casella vuota significa che in quel giorno il punto vendita non riceve merce dal centro
          di distribuzione.
        </p>
      </Card>

      <Card title={`Deroghe dal ${formatDate(from)} al ${formatDate(to)}`}>
        {derogations && derogations.entries.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Punto vendita</th>
                  <th>Data</th>
                  <th>Fascia</th>
                </tr>
              </thead>
              <tbody>
                {derogations.entries.map((d, i) => (
                  <tr key={`${d.store}-${d.date}-${i}`}>
                    <td>
                      <span className="muted">{d.store}</span> {storeName(d.store)}
                    </td>
                    <td>{formatDate(d.date)}</td>
                    <td>
                      <Badge tone={d.slot === 'Morning' ? 'info' : 'warn'}>{slotLabel(d.slot)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>
            Nessuna deroga nei prossimi tre mesi: vale la settimana tipo.
          </Empty>
        )}
      </Card>
    </>
  )
}

function slotLabel(slot: string) {
  switch (slot) {
    case 'Morning':
    case 'morning':
      return 'Entro le 14'
    case 'Afternoon':
    case 'afternoon':
      return 'Dalle 14'
    default:
      return 'Nessuna'
  }
}

function HolidaysSection() {
  const [items, setItems] = useState<Holiday[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [year, setYear] = useState(new Date().getFullYear())

  const [date, setDate] = useState('')
  const [description, setDescription] = useState('')
  const [saveError, setSaveError] = useState<unknown>()

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api
      .get<Holiday[]>('/config/holidays', { year })
      .then(setItems)
      .catch(setError)
      .finally(() => setLoading(false))
  }, [year])

  useEffect(load, [load])

  const add = async () => {
    setSaveError(undefined)
    try {
      await api.put(`/config/holidays/${date}`, { description })
      setDate('')
      setDescription('')
      load()
    } catch (e) {
      setSaveError(e)
    }
  }

  const remove = async (d: string) => {
    setSaveError(undefined)
    try {
      await api.del(`/config/holidays/${d}`)
      load()
    } catch (e) {
      setSaveError(e)
    }
  }

  return (
    <Card
      title="Giorni festivi nazionali"
      actions={
        <div className="inline">
          <label className="muted">Anno</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ width: 100 }}
          />
        </div>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        I giorni festivi spostano in avanti le date di consegna calcolate.
      </p>

      <div className="inline" style={{ alignItems: 'flex-end', marginBottom: 16 }}>
        <Field label="Data">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Descrizione">
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="es. Ferragosto" />
        </Field>
        <Button variant="primary" onClick={add} disabled={!date || description.trim().length < 2}>
          Aggiungi
        </Button>
      </div>

      {saveError ? <ErrorBox error={saveError} /> : null}

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBox error={error} onRetry={load} />
      ) : items && items.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrizione</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((h) => (
                <tr key={h.date}>
                  <td>{formatDate(h.date)}</td>
                  <td>{h.description}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button variant="ghost" onClick={() => remove(h.date)}>
                      Elimina
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>Nessun giorno festivo registrato per il {year}.</Empty>
      )}
    </Card>
  )
}

function LimitsSection() {
  const [items, setItems] = useState<SupplierLimits[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api.get<SupplierLimits[]>('/config/supplier-limits').then(setItems).catch(setError).finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  if (loading) return <Spinner />
  if (error) return <ErrorBox error={error} onRetry={load} />

  return (
    <Card title="Limiti per fornitore">
      <p className="muted" style={{ marginTop: 0 }}>
        Quante righe aggiunte a mano e quante righe da ordine speciale sono ammesse in una proposta.
        Un limite non impostato significa nessun vincolo. Esiste anche un tetto di valore sugli
        ordini speciali: qui si vede solo se è impostato, perché gli importi non sono esposti.
      </p>

      {items && items.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Fornitore</th>
                <th className="num">Righe aggiunte</th>
                <th className="num">Righe ordine speciale</th>
                <th>Tetto di valore</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.supplier}>
                  <td>{l.supplier}</td>
                  <td className="num">
                    <Num value={l.maxManualLines} />
                  </td>
                  <td className="num">
                    <Num value={l.maxSpecialLines} />
                  </td>
                  <td>
                    {typeof l.maxValue === 'number' ?
                      <Badge tone="info">impostato</Badge>
                    : <span className="muted">nessun tetto</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>Nessun limite impostato: tutti i fornitori sono senza vincoli.</Empty>
      )}
    </Card>
  )
}

function LeadTimesSection() {
  const [items, setItems] = useState<LeadTimeRule[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api.get<LeadTimeRule[]>('/config/lead-times').then(setItems).catch(setError).finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  if (loading) return <Spinner />
  if (error) return <ErrorBox error={error} onRetry={load} />

  return (
    <Card title="Tempi medi di consegna">
      <p className="muted" style={{ marginTop: 0 }}>
        Giorni lavorativi fra ordine e consegna. Le regole si applicano dalla più specifica alla più
        generica: fornitore con punto vendita, poi fornitore, poi provincia, poi regione.
      </p>

      {items && items.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Fornitore</th>
                <th>Punto vendita</th>
                <th>Provincia</th>
                <th>Regione</th>
                <th className="num">Giorni</th>
                <th>Origine</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>{r.supplier ?? <span className="muted">tutti</span>}</td>
                  <td>{r.store ?? <span className="muted">tutti</span>}</td>
                  <td>{r.provinceCode ?? <span className="muted">—</span>}</td>
                  <td>{r.regionCode ?? <span className="muted">—</span>}</td>
                  <td className="num">
                    <Num value={r.workingDays} />
                  </td>
                  <td>
                    <Badge tone="muted">{r.source === 'import' ? 'importata' : 'manuale'}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>
          Nessun tempo medio configurato: le date di consegna usano il valore predefinito del fornitore.
        </Empty>
      )}
    </Card>
  )
}

function ReasonsSection() {
  const [items, setItems] = useState<RejectionReason[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api
      .get<RejectionReason[]>('/config/rejection-reasons', { activeOnly: false })
      .then(setItems)
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  if (loading) return <Spinner />
  if (error) return <ErrorBox error={error} onRetry={load} />

  return (
    <Card title="Motivazioni di rifiuto del documento di carico">
      {items && items.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Codice</th>
                <th>Descrizione</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>{r.code}</td>
                  <td>{r.description}</td>
                  <td>
                    <Badge tone={r.isActive ? 'ok' : 'muted'}>{r.isActive ? 'Attiva' : 'Non attiva'}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>
          Nessuna motivazione configurata. Senza motivazioni il back-office non può respingere un
          documento di carico: vanno inserite prima di usare il controllo documenti.
        </Empty>
      )}
    </Card>
  )
}
